import { useEffect, useRef } from "react";
import type { Control } from "react-hook-form";
import { useController } from "react-hook-form";

import TextField from "@/components/TextField";
import useSpeechToText from "@/hooks/useSpeechToText";
import { dictationErrorMessage, TALK_IT_OUT_UNSUPPORTED_MESSAGE } from "@/lib/dictation";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";

import { focusPlanTextField } from "./_AutoFocusedPlanField";
import TalkItOutButton from "./_TalkItOutButton";
import type { CareerPlanOnboardingFormValues } from "./careerPlanOnboarding";

export default function CareerPlanOnboardingNotesStep({
  control
}: {
  control: Control<CareerPlanOnboardingFormValues>;
}) {
  const { field } = useController({ control, name: "consultantNotes" });
  const fieldRef = useRef<HTMLInputElement | null>(null);
  const { listening, toggle } = useSpeechToText({
    value: field.value,
    onChange: field.onChange,
    onUnsupported: () => showSnackbar(TALK_IT_OUT_UNSUPPORTED_MESSAGE),
    onError: (error) => {
      const message = dictationErrorMessage(error);
      if (message) showErrorSnackbar(message);
    }
  });

  useEffect(() => {
    return focusPlanTextField(fieldRef.current);
  }, []);

  return (
    <div className="flex flex-col gap-6" data-testid="career-plan-onboarding-notes">
      <p className="text-headline-small">
        Add general notes about this person that should be included in generation of the plan.
      </p>
      <div className="flex flex-col gap-4">
        <TextField
          name={field.name}
          value={field.value}
          onChangeText={field.onChange}
          onBlur={field.onBlur}
          label="Consultant Notes"
          type="textarea"
          rows={12}
          className="w-full"
          disabled={listening}
          ref={(element) => {
            field.ref(element);
            fieldRef.current = element;
          }}
        />
        <TalkItOutButton listening={listening} onClick={toggle} testId="career-plan-onboarding-talk-it-out" />
      </div>
    </div>
  );
}
